import React, { useState } from 'react';
import { 
  Badge, 
  Box, 
  Tooltip, 
  IconButton, 
  Popover, 
  List, 
  ListItem, 
  ListItemText,
  Typography,
  Paper 
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getFailedRfqEmails } from '../../api/client';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from "../../api/queryKeys";

// Define the interface for a failed email
interface FailedEmail {
  uid: string;
  messageId: string;
  subject: string;
  from: string;
  date: string;
}

const FailedRfqAlertCard: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  
  // Query for failed RFQ emails
  const { data: failedEmails = [], isLoading } = useQuery<FailedEmail[]>({
    queryKey: queryKeys.failedRfqEmails,
    queryFn: getFailedRfqEmails,
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    refetchOnWindowFocus: true,
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'failed-rfq-popover' : undefined;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton disabled>
          <Badge badgeContent={0} color="default">
            <ErrorOutlineIcon />
          </Badge>
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title="Failed RFQ Emails">
        <IconButton
          aria-describedby={id}
          onClick={handleClick}
          size="medium"
          sx={(theme) => ({
            color: failedEmails.length > 0 ? 'error.main' : 'text.secondary',
            '&:hover': {
              backgroundColor: failedEmails.length > 0
                ? alpha(theme.palette.error.main, 0.06)
                : theme.palette.action.hover,
            },
          })}
        >
          <Badge 
            badgeContent={failedEmails.length} 
            color={failedEmails.length > 0 ? "error" : "default"}
            showZero
          >
            <ErrorOutlineIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Paper sx={{ p: 2, maxWidth: 400, maxHeight: 300, overflow: 'auto' }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Failed RFQ Emails
          </Typography>
          {failedEmails.length > 0 ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                These RFQ emails could not be processed. Please check the emails and process them manually.
              </Typography>
              <List dense>
                {failedEmails.map((email) => (
                  <ListItem key={email.uid}>
                    <ListItemText
                      primary={email.subject}
                      secondary={`From: ${email.from} • ${new Date(email.date).toLocaleString()}`}
                      primaryTypographyProps={{ noWrap: true }}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <Typography variant="body2">
              No failed RFQ emails found. All emails have been processed successfully.
            </Typography>
          )}
        </Paper>
      </Popover>
    </Box>
  );
};

export default FailedRfqAlertCard; 